import { ICalloutContentStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPPrimaryButton, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AddStringValueModal } from 'components/StringValue/AddStringValueModal';
import CopilotMatchingStringValuesModal from 'components/StringValue/CopilotMatchingStringValuesModal';
import StringValueList from 'components/StringValue/StringValueList';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import { StringValue_Translation } from 'models/stringValue/stringValue_Translation';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAddStringValue, apiGetStringValues } from 'services/Api/stringValueService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnString } from 'utils/sorting';
import { IPTarget } from "baseComponents/Utils/Target";

interface IStringValuePickerProps {
  isOpen: boolean;
  target: IPTarget;
  onSelect: (items: StringValue[]) => void;
  onDismiss: () => void;
  existingStringValues: number[];
  stringValueType: StringValueTypes;
  canCreateNew: boolean;
}

const StringValuePicker = (props: IStringValuePickerProps) => {
  const { t } = useTranslation(['translation', 'stringvalue']);
  const appContext = useContext(AppContext);
  const [stringValues, setStringValues] = useState<StringValue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedStringValues, setSelectedStringValues] = useState<StringValue[]>([]);
  const [addStringValueModalOpen, setAddStringValueModalOpen] = useState<boolean>(false);
  const [matchingStringValues, setMatchingStringValues] = useState<StringValue[]>([]);
  const [showSelectMatchingStringValues, setShowSelectMatchingStringValues] = useState<StringValue | undefined>(
    undefined,
  );

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const stringValues: StringValue[] = await apiGetStringValues(props.stringValueType, accessToken);

      stringValues.sort((a, b) => sortOnString(a.value, b.value));
      setStringValues(stringValues);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: '10px 12px',
      minHeight: 300,
      height: '50vh',
      maxHeight: '80vh',
      minWidth: 300,
      width: '50vw',
    },
  };

  const createNewStringValue = async (newStringValue: StringValue, matchingStringValues: StringValue[] | undefined) => {
    try {
      if (matchingStringValues && matchingStringValues.length > 0) {
        setMatchingStringValues(matchingStringValues);
        setShowSelectMatchingStringValues(newStringValue);

        return;
      }

      setIsLoading(true);

      newStringValue.stringTypeId = props.stringValueType;
      newStringValue.trans = [new StringValue_Translation()];
      newStringValue.transIdx = 0;
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const stringValue = await apiAddStringValue(newStringValue, accessToken);

      setStringValues((prev) => [...prev, stringValue]);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onDismiss = () => {
    setSelectedStringValues([]);
    props.onDismiss();
  };

  //
  // Main render
  //
  return (
    <IPCallout
      styles={calloutStyle}
      hidden={!props.isOpen}
      setInitialFocus
      gapSpace={0}
      target={props.target}
      onDismiss={() => onDismiss()}
      dismissOnTargetClick
    >
      <IPStack verticalFill>
        {!isLoading && props.canCreateNew && stringValues.length === 0 && (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <IPLink
              onClick={() => {
                setAddStringValueModalOpen(true);
              }}
            >
              {t('stringValue:AddStringValue.Link', { type: StringValue.getTypeText(props.stringValueType, true, t) })}
            </IPLink>
          </IPStack>
        )}
        {!isLoading && !props.canCreateNew && stringValues.length === 0 && (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <IPText>
              {t('stringValue:AddStringValue.CannotAdd', {
                type: StringValue.getTypeText(props.stringValueType, true, t),
              })}
            </IPText>
          </IPStack>
        )}
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && stringValues.length > 0 && (
          <IPStack.Item grow>
            <StringValueList
              stringValues={stringValues}
              existingStringValues={props.existingStringValues}
              isLoading={isLoading}
              onSelectStringValues={(items) => setSelectedStringValues(items)}
              onAddNewStringValue={() => setAddStringValueModalOpen(true)}
              type={props.stringValueType}
              canCreateNew={props.canCreateNew}
            />
          </IPStack.Item>
        )}
        <IPSeparator />
        <IPStack.Item>
          <IPPrimaryButton
            disabled={selectedStringValues === undefined || selectedStringValues.length === 0}
            onClick={() => {
              if (selectedStringValues) {
                props.onSelect(selectedStringValues);
                onDismiss();
              }
            }}
          >
            {t('translation:General.Button.Select')}
          </IPPrimaryButton>
        </IPStack.Item>
        <AddStringValueModal
          isOpen={addStringValueModalOpen}
          onDismiss={() => setAddStringValueModalOpen(false)}
          stringValueType={props.stringValueType}
          onCreate={createNewStringValue}
        />
        {showSelectMatchingStringValues !== undefined && (
          <CopilotMatchingStringValuesModal
            isOpen={showSelectMatchingStringValues !== undefined}
            onClose={() => setShowSelectMatchingStringValues(undefined)}
            newStringValue={showSelectMatchingStringValues}
            matchingStringValues={matchingStringValues.map((sv) => sv.stringValueId)}
            existingStringValues={props.existingStringValues}
            onSelect={(selectedStringValue: StringValue, isNew: boolean) => {
              setShowSelectMatchingStringValues(undefined);
              if (isNew) {
                createNewStringValue(selectedStringValue, undefined);
              } else {
                props.onSelect([selectedStringValue]);
                onDismiss();
              }
            }}
          />
        )}
      </IPStack>
    </IPCallout>
  );
};

export default StringValuePicker;
